/**
 * Encounter type picker.
 *
 * Mounted by `EncounterTypeDispatcher` when the destination type cannot be
 * resolved deterministically — i.e. the user is creating an encounter with
 * either no `restrictToEncounterTypes` (fully free creation) or a
 * multi-element list (activity-array map or `PatientFloatingActionButton`'s
 * non-intake list).
 *
 * Renders the same `EncounterTypeSection` UI the legacy form did. Once the
 * user picks a type, the parent re-renders with that single-element
 * `restrictToEncounterTypes`, which the dispatcher resolves to the
 * corresponding typed form.
 *
 * @agentic connections
 */

import { useState } from "react";
import { Skeleton, Stack } from "@mantine/core";
import {
  IconHeartHandshake,
  IconMessage,
  IconSocial,
  IconUser,
} from "@tabler/icons-react";
import type { Patient } from "@medplum/fhirtypes";

import {
  ALL_ENCOUNTER_TYPE_SELECT_OPTIONS,
  getFreeCreationNonIntakeEncounterTypes,
} from "@benchmark/core/constants";

import { usePatientResource } from "@/hooks/shared/useFHIRResource";
import type { EnhancedSelectOption } from "@/types/ui/component-props";
import type { ReactNode } from "react";

import { EncounterTypeSection } from "./sections/EncounterTypeSection";

const ENCOUNTER_TYPE_ICON: Record<string, ReactNode> = {
  "general-interaction": <IconUser size={20} />,
  "case-management-interaction": <IconSocial size={20} />,
  "clinical-intake-interaction": <IconHeartHandshake size={20} />,
  "clinical-follow-up-interaction": <IconHeartHandshake size={20} />,
  "cocm-intake-interaction": <IconMessage size={20} />,
  "cocm-follow-up-interaction": <IconMessage size={20} />,
};

const ALL_TYPE_OPTIONS_WITH_ICONS: EnhancedSelectOption[] =
  ALL_ENCOUNTER_TYPE_SELECT_OPTIONS.map((opt) => ({
    ...opt,
    label: opt.deprecated ? `${opt.label} (Legacy)` : opt.label,
    icon: ENCOUNTER_TYPE_ICON[opt.value] ?? <IconMessage size={20} />,
  }));

/**
 * Props for {@link EncounterTypePicker}.
 *
 * @agentic connections
 */
export type EncounterTypePickerProps = {
  patientId?: string;
  /** Multi-element list of allowed types. Empty/undefined = full v2 list. */
  allowedTypes?: string[];
  onPick: (type: string) => void;
};

/**
 * Renders a card-select for encounter types, then calls `onPick` once the
 * user makes a choice.
 *
 * @agentic connections
 */
export const EncounterTypePicker = ({
  patientId,
  allowedTypes,
  onPick,
}: EncounterTypePickerProps) => {
  const [pending, setPending] = useState<string>("");
  const { data: patientResource } = usePatientResource(patientId);
  const patient = patientResource as Patient | undefined;

  // Intake types are never free-created — they are reached through the
  // `complete-intake` activity (billing is decided at Schedule Intake). The
  // default free-creation list therefore excludes all intake codes.
  const baseAllowed = allowedTypes ?? getFreeCreationNonIntakeEncounterTypes();

  const enrichedOptions: EnhancedSelectOption[] = baseAllowed
    .map((code) => ALL_TYPE_OPTIONS_WITH_ICONS.find((o) => o.value === code))
    .filter((opt): opt is EnhancedSelectOption => opt !== undefined);

  if (!patient) {
    return (
      <Stack gap="md" data-testid="encounter-type-picker-loading">
        <Skeleton height={32} width="40%" />
        <Skeleton height={120} />
      </Stack>
    );
  }

  return (
    <div data-testid="encounter-type-picker">
      <EncounterTypeSection
        value={pending}
        hasLegacyEncounterType={false}
        allEncounterTypeOptions={ALL_TYPE_OPTIONS_WITH_ICONS}
        currentEncounterTypeOptions={enrichedOptions}
        allowedTypes={baseAllowed}
        readOnly={false}
        onChange={(value) => {
          setPending(value);
          if (value) onPick(value);
        }}
      />
    </div>
  );
};
